import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { AlineTooltip } from 'src/components';
import { ActivityEditorFooter } from 'src/components/activity/ActivityEditorFooter';
import {
  addMentionEntityIdsFromMarkdown,
  createMentionEntityIdSets,
  toMentionEntityIds,
} from 'src/components/ai/chat/chat-hooks/mention-entity-id-extraction';
import {
  AccessRequestStatusContext,
  MentionEntityContext,
  useAccessRequestStatus,
} from 'src/components/ai/chat/chat-hooks/useMentionAccessContext';
import { useMentionEntitiesQuery } from 'src/components/ai/chat/chat-hooks/useMentionEntitiesQuery';
import { AlineMarkdownEditor } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/AlineMarkdownEditor';
import type {
  AlineMarkdownEditorRef,
  MentionEntitiesConfig,
} from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/types';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

import { ActivityLaneSwitch } from './task-review/ActivityLaneSwitch';
import type { ActivityLane } from './task-review/use-activity-lane';
import { ACTIVITY_LANE } from './task-review/use-activity-lane';

const useStyles = makeStyles()(theme => ({
  caption: {
    color: theme.appColors.iconsTypeMuted,
    fontSize: 12,
  },
  /**
   * Editable description matches the comment field's white card; the
   * read-only (completed) view keeps the panel background by design.
   */
  editor: {
    backgroundColor: theme.palette.common.white,
  },
  editorWrap: {
    position: 'relative',
    width: '100%',
  },
  labelRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sectionLabel: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
  },
}));

type DescriptionField = 'description' | 'externalDescription';

const SIDE_COPY = {
  [ACTIVITY_LANE.External]: {
    caption: 'Shown to external reviewers as their instructions.',
    editPlaceholder: 'Add instructions for external reviewers…',
    field: 'externalDescription',
    mentionTooltip: null,
    readOnlyPlaceholder: 'No instructions for external reviewers.',
  },
  [ACTIVITY_LANE.Team]: {
    caption: 'Internal description for your organization and Aline AI.',
    editPlaceholder: 'Add a description, type @ to mention someone…',
    field: 'description',
    mentionTooltip: 'Add context',
    readOnlyPlaceholder: 'No task description.',
  },
} as const satisfies Record<
  ActivityLane,
  {
    caption: string;
    editPlaceholder: string;
    field: DescriptionField;
    // Null when the side takes plain markdown, with no mentions.
    mentionTooltip: string | null;
    readOnlyPlaceholder: string;
  }
>;

interface TaskDescriptionFieldProps {
  description: string;
  externalDescription: string;
  isReadOnly: boolean;
  taskId: UUID;
  workflowId: UUID;
  editDisabledReason?: string | null;
  /** Replaces the "Description" label, e.g. with the task page's card title. */
  heading?: React.ReactNode;
}

/**
 * The task's two descriptions behind a Team / External switch: Team is the internal brief and the
 * Aline AI prompt, External is what external reviewers read as their instructions.
 */
export const TaskDescriptionField = React.memo(function TaskDescriptionField({
  description,
  editDisabledReason,
  externalDescription,
  heading,
  isReadOnly,
  taskId,
  workflowId,
}: TaskDescriptionFieldProps) {
  const { classes } = useStyles();
  const [side, setSide] = useState<ActivityLane>(ACTIVITY_LANE.Team);
  const copy = SIDE_COPY[side];

  return (
    <div className={classes.row}>
      <div className={classes.labelRow}>
        {heading ?? <span className={classes.sectionLabel}>Description</span>}
        <ActivityLaneSwitch ariaLabel="Description audience" lane={side} onChange={setSide} />
      </div>
      <span className={classes.caption}>{copy.caption}</span>
      <TaskMarkdownField
        key={side}
        editDisabledReason={editDisabledReason}
        isReadOnly={isReadOnly}
        side={side}
        taskId={taskId}
        value={copy.field === 'description' ? description : externalDescription}
        workflowId={workflowId}
      />
    </div>
  );
});

function TaskMarkdownField({
  editDisabledReason,
  isReadOnly,
  side,
  taskId,
  value: description,
  workflowId,
}: {
  isReadOnly: boolean;
  side: ActivityLane;
  taskId: UUID;
  value: string;
  workflowId: UUID;
  editDisabledReason?: string | null;
}) {
  const { classes } = useStyles();
  const copy = SIDE_COPY[side];
  const { field } = copy;
  const [updateDescription] = Apollo.useUpdateTaskDescriptionMarkdownMutation();
  const [updateExternalDescription] = Apollo.useUpdateTaskExternalDescriptionMarkdownMutation();
  const updateMarkdown = field === 'description' ? updateDescription : updateExternalDescription;
  /**
   * `epoch` keys the editable editor: AlineMarkdownEditor reads `value` only
   * on mount, so applying an external change (another client, the AI task
   * subagent, a push-invalidation refetch) requires a remount. User keystrokes
   * update `markdown` without bumping the epoch, so typing never remounts the
   * editor.
   */
  const [editorValue, setEditorValue] = useState({
    epoch: 0,
    markdown: description,
  });
  const editorValueRef = useRef(editorValue);
  editorValueRef.current = editorValue;
  const editorRef = useRef<AlineMarkdownEditorRef>(null);
  // The team side mentions any workflow entity; external instructions are plain markdown.
  const mentions = useMemo(
    () => (field === 'description' ? { allowIntakeFormMention: true, workflowId } : undefined),
    [field, workflowId]
  );
  /**
   * True while a user edit is pending its debounced write — gates the
   * external-prop sync effect so the server echo can't clobber typing.
   */
  const hasPendingWriteRef = useRef(false);
  /** Latest markdown queued for a debounced write — used to avoid clearing the pending flag early. */
  const latestPendingMarkdownRef = useRef<string | null>(null);
  /**
   * The markdown most recently sent to the server. The editor's serialization
   * isn't byte-stable against its own parse (normalization), so the refetch
   * echo of our own save can differ from the prop we mounted with — that echo
   * must be absorbed, not treated as an external change, or save → refetch →
   * remount → normalize-onChange → save loops forever (the "blinking drawer").
   */
  const lastSavedMarkdownRef = useRef<string | null>(null);
  const debouncedUpdateMarkdown = useDebouncedCallback(async (markdown: string) => {
    try {
      lastSavedMarkdownRef.current = markdown;
      const { error } = await updateMarkdown({ variables: { markdown, taskId } });
      /**
       * The save failed, so this value was never persisted — it must not absorb
       * future echoes as if it had been. The user's unsaved text stays in the
       * editor until a genuinely different external value arrives; the errorLink
       * toast already reported the failure.
       */
      if (error) lastSavedMarkdownRef.current = null;
    } catch {
      lastSavedMarkdownRef.current = null;
      /**
       * GraphQL failures are surfaced by the global Apollo errorLink (toast +
       * Sentry); swallow here so the debounced call doesn't reject unhandled.
       */
    } finally {
      if (latestPendingMarkdownRef.current === markdown) {
        hasPendingWriteRef.current = false;
        latestPendingMarkdownRef.current = null;
      }
    }
  }, 500);

  const handleDescriptionChange = useCallback(
    (markdown: string) => {
      /**
       * The editor fires onChange during mount/normalization, not only on user
       * edits. A no-op change must not schedule a write: combined with the echo
       * absorption below, that's what breaks the save/refetch feedback loop.
       */
      if (markdown === editorValueRef.current.markdown) return;
      hasPendingWriteRef.current = true;
      latestPendingMarkdownRef.current = markdown;
      setEditorValue(prev => ({ ...prev, markdown }));
      void debouncedUpdateMarkdown(markdown);
    },
    [debouncedUpdateMarkdown]
  );

  /**
   * Flush any pending debounced save on unmount — switching tasks or closing the panel unmounts
   * this field, and use-debounce drops (does not flush) a queued call on unmount, silently losing
   * the last keystrokes.
   */
  useEffect(() => () => debouncedUpdateMarkdown.flush(), [debouncedUpdateMarkdown]);

  /**
   * Sync external updates into the editor when the user is idle — another client editing, the
   * push-invalidation refetch, or the AI task subagent calling updateTaskDescription. Gated on
   * hasPendingWriteRef so a server echo can't stomp the user's in-progress typing, and the epoch
   * only bumps (remounting the editor) when the content actually differs, so a same-value echo
   * after the user's own save is a no-op.
   */
  useEffect(() => {
    if (hasPendingWriteRef.current) return;
    setEditorValue(prev => {
      if (prev.markdown === description) return prev;
      /**
       * Our own save echoed back: the editor already renders equivalent
       * content, so absorb the server's serialization silently instead of
       * remounting (which would blink the editor and re-trigger onChange).
       */
      if (description === lastSavedMarkdownRef.current) {
        return { ...prev, markdown: description };
      }
      return { epoch: prev.epoch + 1, markdown: description };
    });
  }, [description]);

  /**
   * Flush any pending save when the task is marked complete. Completion flips isReadOnly in-place
   * (no unmount), so the unmount flush above won't run, and the read-only view would show the
   * stale server description until the next refetch.
   */
  useEffect(() => {
    if (isReadOnly) debouncedUpdateMarkdown.flush();
  }, [isReadOnly, debouncedUpdateMarkdown]);

  return (
    <TaskDescriptionMentionContextProvider markdown={editorValue.markdown}>
      <TaskDescriptionFieldBody
        classes={classes}
        copy={copy}
        description={description}
        editDisabledReason={editDisabledReason}
        editorRef={editorRef}
        editorValue={editorValue}
        handleDescriptionChange={handleDescriptionChange}
        isReadOnly={isReadOnly}
        mentions={mentions}
      />
    </TaskDescriptionMentionContextProvider>
  );
}

function TaskDescriptionMentionContextProvider({
  children,
  markdown,
}: {
  children: React.ReactNode;
  markdown: string;
}) {
  const mentionEntityIds = useMemo(() => {
    const ids = createMentionEntityIdSets();
    addMentionEntityIdsFromMarkdown(ids, markdown);
    return toMentionEntityIds(ids);
  }, [markdown]);
  const { inaccessibleDocumentIds, mentionEntityContextValue } =
    useMentionEntitiesQuery(mentionEntityIds);
  const {
    refetch: refetchAccessRequestStatus,
    statusMap: accessRequestStatusMap,
    updateStatus,
  } = useAccessRequestStatus(inaccessibleDocumentIds);
  const accessRequestStatusContextValue = useMemo(
    () => ({
      refetch: refetchAccessRequestStatus,
      statusMap: accessRequestStatusMap,
      updateStatus,
    }),
    [accessRequestStatusMap, refetchAccessRequestStatus, updateStatus]
  );

  return (
    <MentionEntityContext.Provider value={mentionEntityContextValue}>
      <AccessRequestStatusContext.Provider value={accessRequestStatusContextValue}>
        {children}
      </AccessRequestStatusContext.Provider>
    </MentionEntityContext.Provider>
  );
}

function TaskDescriptionFieldBody({
  classes,
  copy,
  description,
  editDisabledReason,
  editorRef,
  editorValue,
  handleDescriptionChange,
  isReadOnly,
  mentions,
}: {
  classes: ReturnType<typeof useStyles>['classes'];
  copy: (typeof SIDE_COPY)[ActivityLane];
  description: string;
  editorRef: React.RefObject<AlineMarkdownEditorRef | null>;
  editorValue: { epoch: number; markdown: string };
  handleDescriptionChange: (markdown: string) => void;
  isReadOnly: boolean;
  mentions: MentionEntitiesConfig | undefined;
  editDisabledReason?: string | null;
}) {
  if (isReadOnly)
    return (
      <AlineMarkdownEditor
        /**
         * The editor reads `value` once on mount; re-key on the rendered value so a server-side
         * change to a read-only task's description is reflected.
         */
        key={description}
        hideToolbar
        noBorder
        readOnly
        maxHeight={280}
        minHeight={0}
        placeholder={copy.readOnlyPlaceholder}
        value={description}
      />
    );

  return (
    <AlineTooltip arrow placement="top" title={editDisabledReason ?? ''}>
      <div className={classes.editorWrap}>
        <AlineMarkdownEditor
          key={editorValue.epoch}
          ref={editorRef}
          floatingToolbar
          hideToolbar
          showToolbarOnSelection
          bottomContent={
            typeof copy.mentionTooltip === 'string' && (
              <ActivityEditorFooter
                mentionDisabled={Boolean(editDisabledReason)}
                mentionTooltip={copy.mentionTooltip}
                showSubmit={false}
                onOpenMentions={() => editorRef.current?.openMentionPicker()}
              />
            )
          }
          className={classes.editor}
          disabled={Boolean(editDisabledReason)}
          maxHeight={280}
          mentions={mentions}
          minHeight={120}
          placeholder={copy.editPlaceholder}
          style={{ borderRadius: 12 }}
          toolbarStyle={{ backgroundColor: 'transparent' }}
          value={editorValue.markdown}
          onChange={handleDescriptionChange}
        />
      </div>
    </AlineTooltip>
  );
}
